import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { billingApi } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import {
  CheckIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CreditCardIcon,
  CalendarIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

interface PlanLimits {
  maxAffiliates: number;
  maxMonthlyTrackedRevenue: number;
  maxCampaigns: number;
  maxLinks: number;
  customDomain: boolean;
  apiAccess: boolean;
  webhooks: boolean;
  whiteLabel: boolean;
  prioritySupport: boolean;
  dedicatedManager: boolean;
  customIntegrations: boolean;
  advancedAnalytics: boolean;
  dataExports: boolean;
}

interface PricingPlan {
  tier: string;
  name: string;
  description: string;
  monthlyPrice: number;
  yearlyPrice: number;
  platformFeePercent: number;
  limits: PlanLimits;
  features: string[];
  highlighted?: boolean;
}

interface Subscription {
  subscriptionId: string;
  organizationId: string;
  tier: string;
  status: 'active' | 'trialing' | 'past_due' | 'canceled' | 'unpaid';
  billingInterval: 'monthly' | 'yearly';
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  trialEnd?: string;
  canceledAt?: string;
}

export default function Billing() {
  const [searchParams] = useSearchParams();
  const { currentOrganization: _currentOrganization } = useAuthStore();

  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [currentPlan, setCurrentPlan] = useState<PricingPlan | null>(null);
  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>('monthly');
  const [showChangePlan, setShowChangePlan] = useState(false);

  useEffect(() => {
    loadSubscription();
    loadPlans();

    // Handle success/cancel from Stripe checkout
    if (searchParams.get('success') === 'true') {
      toast.success('Subscription created successfully!');
    } else if (searchParams.get('canceled') === 'true') {
      toast.error('Checkout was canceled');
    }
  }, [searchParams]);

  const loadSubscription = async () => {
    try {
      const response = await billingApi.getSubscription();
      if (response.data.success) {
        setSubscription(response.data.data.subscription);
        setCurrentPlan(response.data.data.currentPlan);
        if (response.data.data.subscription?.billingInterval) {
          setBillingInterval(response.data.data.subscription.billingInterval);
        }
      }
    } catch (error) {
      console.error('Failed to load subscription:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadPlans = async () => {
    try {
      const response = await billingApi.getPlans();
      if (response.data.success) {
        setPlans(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load plans:', error);
    }
  };

  const handleOpenPortal = async () => {
    setIsProcessing(true);
    try {
      const response = await billingApi.getPortalSession();
      if (response.data.success) {
        window.location.href = response.data.data.url;
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { message?: string } } } };
      toast.error(err.response?.data?.error?.message || 'Failed to open billing portal');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!confirm('Are you sure you want to cancel your subscription? You will retain access until the end of your billing period.')) {
      return;
    }

    setIsProcessing(true);
    try {
      const response = await billingApi.cancelSubscription();
      if (response.data.success) {
        toast.success('Subscription will be canceled at the end of the billing period');
        loadSubscription();
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { message?: string } } } };
      toast.error(err.response?.data?.error?.message || 'Failed to cancel subscription');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleResumeSubscription = async () => {
    setIsProcessing(true);
    try {
      const response = await billingApi.resumeSubscription();
      if (response.data.success) {
        toast.success('Subscription resumed successfully');
        loadSubscription();
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { message?: string } } } };
      toast.error(err.response?.data?.error?.message || 'Failed to resume subscription');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleChangePlan = async (tier: string) => {
    if (tier === currentPlan?.tier) {
      toast.error('You are already on this plan');
      return;
    }

    if (tier === 'starter') {
      toast.error('To downgrade to the free plan, please cancel your subscription');
      return;
    }

    if (tier === 'enterprise') {
      window.location.href = 'mailto:sales@venndela.com?subject=Enterprise%20Plan%20Upgrade';
      return;
    }

    const targetPlan = plans.find(p => p.tier === tier);
    const isUpgrade = (targetPlan?.monthlyPrice || 0) > (currentPlan?.monthlyPrice || 0);

    if (!confirm(`Are you sure you want to ${isUpgrade ? 'upgrade' : 'downgrade'} to the ${targetPlan?.name} plan?`)) {
      return;
    }

    setIsProcessing(true);
    try {
      const response = await billingApi.changePlan(tier, billingInterval);
      if (response.data.success) {
        toast.success(response.data.data.message);
        setShowChangePlan(false);
        loadSubscription();
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { message?: string } } } };
      toast.error(err.response?.data?.error?.message || 'Failed to change plan');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSubscribe = async (tier: string) => {
    if (tier === 'starter' || tier === 'enterprise') return;

    setIsProcessing(true);
    try {
      const response = await billingApi.createCheckout(tier, billingInterval);
      if (response.data.success) {
        window.location.href = response.data.data.url;
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { message?: string } } } };
      toast.error(err.response?.data?.error?.message || 'Failed to create checkout session');
    } finally {
      setIsProcessing(false);
    }
  };

  const formatPrice = (cents: number) => {
    if (cents === -1) return 'Custom';
    if (cents === 0) return 'Free';
    return `$${(cents / 100).toFixed(0)}`;
  };

  const getYearlyMonthlyPrice = (yearlyPrice: number) => {
    if (yearlyPrice === -1 || yearlyPrice === 0) return yearlyPrice;
    return Math.round(yearlyPrice / 12);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusBadge = (status: string, cancelAtPeriodEnd: boolean) => {
    if (cancelAtPeriodEnd) {
      return <span className="badge-warning">Canceling</span>;
    }
    switch (status) {
      case 'active':
        return <span className="badge-success">Active</span>;
      case 'trialing':
        return <span className="badge-info">Trial</span>;
      case 'past_due':
        return <span className="badge-danger">Past Due</span>;
      case 'canceled':
        return <span className="badge-neutral">Canceled</span>;
      default:
        return <span className="badge-neutral">{status}</span>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rust"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-display font-bold text-charcoal">Billing</h2>
        <p className="text-charcoal/60 mt-1">Manage your subscription and billing</p>
      </div>

      {/* Current Plan */}
      <div className="rounded-xl bg-warm-white shadow-sm">
        <div className="px-6 py-4 border-b border-tan/30">
          <h3 className="text-lg font-display font-semibold text-charcoal">Current Plan</h3>
        </div>
        <div className="p-6">
          {subscription && currentPlan ? (
            <div className="space-y-6">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-3">
                    <h4 className="text-xl font-bold text-charcoal">{currentPlan.name}</h4>
                    {getStatusBadge(subscription.status, subscription.cancelAtPeriodEnd)}
                  </div>
                  <p className="text-charcoal/60 mt-1">{currentPlan.description}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-serif font-bold text-charcoal">
                    {formatPrice(
                      subscription.billingInterval === 'yearly'
                        ? getYearlyMonthlyPrice(currentPlan.yearlyPrice)
                        : currentPlan.monthlyPrice
                    )}
                    <span className="text-sm text-charcoal/60 font-normal">/mo</span>
                  </p>
                  <p className="text-sm text-charcoal/50">
                    Billed {subscription.billingInterval}
                  </p>
                </div>
              </div>

              {/* Period Info and Platform Fee */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-4 bg-cream/50 rounded-lg">
                  <CalendarIcon className="w-5 h-5 text-charcoal/60" />
                  <div>
                    <p className="text-sm text-charcoal/60">Current Period</p>
                    <p className="text-charcoal">
                      {formatDate(subscription.currentPeriodStart)} - {formatDate(subscription.currentPeriodEnd)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-cream/50 rounded-lg">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    currentPlan.platformFeePercent === 0
                      ? 'bg-sage/20 text-sage'
                      : 'bg-charcoal/10 text-charcoal'
                  }`}>
                    <span className="font-bold">{currentPlan.platformFeePercent}%</span>
                  </div>
                  <div>
                    <p className="text-sm text-charcoal/60">Platform Fee</p>
                    <p className="text-charcoal">
                      {currentPlan.platformFeePercent === 0
                        ? 'No fees on your plan'
                        : `${currentPlan.platformFeePercent}% of commissions`}
                    </p>
                  </div>
                </div>
              </div>

              {/* Upgrade to remove fees */}
              {currentPlan.platformFeePercent > 0 && (
                <div className="p-4 bg-rust/10 border border-rust/30 rounded-lg">
                  <p className="text-charcoal text-sm">
                    <strong>Tip:</strong> Upgrade to Business or Enterprise to eliminate platform fees completely.
                    {currentPlan.platformFeePercent >= 3 && (
                      <> Or upgrade to Pro for just 1% fees.</>
                    )}
                  </p>
                </div>
              )}

              {/* Cancellation Warning */}
              {subscription.cancelAtPeriodEnd && (
                <div className="flex items-start gap-3 p-4 bg-tan/20 border border-tan/50 rounded-lg">
                  <ExclamationTriangleIcon className="w-6 h-6 text-tan flex-shrink-0" />
                  <div>
                    <p className="text-charcoal font-medium">Subscription Canceling</p>
                    <p className="text-charcoal/70 text-sm">
                      Your subscription will end on {formatDate(subscription.currentPeriodEnd)}.
                      You'll be downgraded to the free Starter plan.
                    </p>
                    <button
                      onClick={handleResumeSubscription}
                      disabled={isProcessing}
                      className="mt-3 btn btn-sm bg-tan text-charcoal hover:bg-tan/80"
                    >
                      {isProcessing ? 'Processing...' : 'Resume Subscription'}
                    </button>
                  </div>
                </div>
              )}

              {/* Past Due Warning */}
              {subscription.status === 'past_due' && (
                <div className="flex items-start gap-3 p-4 bg-rust/10 border border-rust/30 rounded-lg">
                  <ExclamationTriangleIcon className="w-6 h-6 text-rust flex-shrink-0" />
                  <div>
                    <p className="text-rust font-medium">Payment Failed</p>
                    <p className="text-charcoal/70 text-sm">
                      Your last payment failed. Please update your payment method to continue your subscription.
                    </p>
                    <button
                      onClick={handleOpenPortal}
                      disabled={isProcessing}
                      className="mt-3 btn btn-sm bg-rust text-white hover:bg-rust/90"
                    >
                      {isProcessing ? 'Processing...' : 'Update Payment Method'}
                    </button>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleOpenPortal}
                  disabled={isProcessing}
                  className="btn btn-secondary"
                >
                  <CreditCardIcon className="w-4 h-4 mr-2" />
                  {isProcessing ? 'Loading...' : 'Manage Payment Method'}
                </button>
                <button
                  onClick={() => setShowChangePlan(true)}
                  className="btn btn-secondary"
                >
                  <ArrowUpIcon className="w-4 h-4 mr-2" />
                  Change Plan
                </button>
                {!subscription.cancelAtPeriodEnd && (
                  <button
                    onClick={handleCancelSubscription}
                    disabled={isProcessing}
                    className="btn text-rust hover:text-rust/80 hover:bg-rust/10"
                  >
                    Cancel Subscription
                  </button>
                )}
              </div>
            </div>
          ) : (
            // No subscription - show starter plan info
            <div className="space-y-6">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-3">
                    <h4 className="text-xl font-bold text-charcoal">Starter</h4>
                    <span className="badge-neutral">Free</span>
                  </div>
                  <p className="text-charcoal/60 mt-1">Perfect for testing and small businesses</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-serif font-bold text-charcoal">$0<span className="text-sm text-charcoal/60 font-normal">/mo</span></p>
                </div>
              </div>

              {/* Platform Fee Info for Starter */}
              <div className="flex items-center gap-3 p-4 bg-cream/50 rounded-lg">
                <div className="w-10 h-10 rounded-lg bg-charcoal/10 flex items-center justify-center text-charcoal">
                  <span className="font-bold">5%</span>
                </div>
                <div>
                  <p className="text-sm text-charcoal/60">Platform Fee</p>
                  <p className="text-charcoal">5% of commissions processed</p>
                </div>
              </div>

              <div className="p-4 bg-rust/10 border border-rust/30 rounded-lg">
                <p className="text-charcoal text-sm">
                  <strong>Upgrade to reduce fees:</strong> Growth (3%), Pro (1%), or Business/Enterprise (0%)
                </p>
              </div>

              <button
                onClick={() => setShowChangePlan(true)}
                className="btn btn-primary"
              >
                <ArrowUpIcon className="w-4 h-4 mr-2" />
                Upgrade Plan
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Plan Limits */}
      {currentPlan && (
        <div className="rounded-xl bg-warm-white shadow-sm">
          <div className="px-6 py-4 border-b border-tan/30">
            <h3 className="text-lg font-display font-semibold text-charcoal">Plan Limits</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-cream/50 rounded-lg">
                <p className="text-sm text-charcoal/60">Affiliates</p>
                <p className="text-xl font-serif font-bold text-charcoal">
                  {currentPlan.limits.maxAffiliates === -1 ? 'Unlimited' : currentPlan.limits.maxAffiliates}
                </p>
              </div>
              <div className="p-4 bg-cream/50 rounded-lg">
                <p className="text-sm text-charcoal/60">Monthly Revenue</p>
                <p className="text-xl font-serif font-bold text-charcoal">
                  {currentPlan.limits.maxMonthlyTrackedRevenue === -1
                    ? 'Unlimited'
                    : `$${(currentPlan.limits.maxMonthlyTrackedRevenue / 100).toLocaleString()}`}
                </p>
              </div>
              <div className="p-4 bg-cream/50 rounded-lg">
                <p className="text-sm text-charcoal/60">Campaigns</p>
                <p className="text-xl font-serif font-bold text-charcoal">
                  {currentPlan.limits.maxCampaigns === -1 ? 'Unlimited' : currentPlan.limits.maxCampaigns}
                </p>
              </div>
              <div className="p-4 bg-cream/50 rounded-lg">
                <p className="text-sm text-charcoal/60">Links</p>
                <p className="text-xl font-serif font-bold text-charcoal">
                  {currentPlan.limits.maxLinks === -1 ? 'Unlimited' : currentPlan.limits.maxLinks}
                </p>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { label: 'Custom Domain', value: currentPlan.limits.customDomain },
                { label: 'API Access', value: currentPlan.limits.apiAccess },
                { label: 'Webhooks', value: currentPlan.limits.webhooks },
                { label: 'White Label', value: currentPlan.limits.whiteLabel },
                { label: 'Advanced Analytics', value: currentPlan.limits.advancedAnalytics },
                { label: 'Data Exports', value: currentPlan.limits.dataExports },
              ].map((feature) => (
                <div key={feature.label} className="flex items-center gap-2">
                  {feature.value ? (
                    <CheckIcon className="w-5 h-5 text-sage" />
                  ) : (
                    <span className="w-5 h-5 text-charcoal/30">-</span>
                  )}
                  <span className={feature.value ? 'text-charcoal' : 'text-charcoal/50'}>
                    {feature.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Change Plan Modal */}
      {showChangePlan && (
        <div className="fixed inset-0 bg-charcoal/50 flex items-center justify-center z-50 p-4">
          <div className="bg-warm-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-tan/30">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-display font-bold text-charcoal">Change Plan</h3>
                <button
                  onClick={() => setShowChangePlan(false)}
                  className="text-charcoal/60 hover:text-charcoal text-xl"
                >
                  &times;
                </button>
              </div>

              {/* Billing Toggle */}
              <div className="mt-4 inline-flex items-center gap-4 bg-cream rounded-full p-1">
                <button
                  onClick={() => setBillingInterval('monthly')}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    billingInterval === 'monthly'
                      ? 'bg-rust text-white'
                      : 'text-charcoal/60 hover:text-charcoal'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingInterval('yearly')}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    billingInterval === 'yearly'
                      ? 'bg-rust text-white'
                      : 'text-charcoal/60 hover:text-charcoal'
                  }`}
                >
                  Yearly (Save 20%)
                </button>
              </div>
            </div>

            <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {plans.filter(p => p.tier !== 'enterprise').map((plan) => {
                const isCurrentPlan = plan.tier === (subscription?.tier || 'starter');
                const targetPrice = billingInterval === 'yearly'
                  ? getYearlyMonthlyPrice(plan.yearlyPrice)
                  : plan.monthlyPrice;
                const currentPrice = billingInterval === 'yearly'
                  ? getYearlyMonthlyPrice(currentPlan?.yearlyPrice || 0)
                  : (currentPlan?.monthlyPrice || 0);
                const isUpgrade = targetPrice > currentPrice;

                return (
                  <div
                    key={plan.tier}
                    className={`rounded-xl p-5 ${
                      isCurrentPlan
                        ? 'ring-2 ring-rust bg-rust/5'
                        : 'bg-cream/50 border border-tan/30'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-charcoal">{plan.name}</h4>
                      {isCurrentPlan && (
                        <span className="text-xs bg-rust text-white px-2 py-0.5 rounded-full">
                          Current
                        </span>
                      )}
                    </div>
                    <p className="text-2xl font-serif font-bold text-charcoal mb-1">
                      {formatPrice(targetPrice)}
                      {plan.monthlyPrice > 0 && <span className="text-sm font-normal text-charcoal/60">/mo</span>}
                    </p>
                    <p className="text-sm text-charcoal/60 mb-4">{plan.description}</p>

                    <ul className="space-y-2 mb-4">
                      {plan.features.slice(0, 4).map((feature, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <CheckIcon className="w-4 h-4 text-sage flex-shrink-0 mt-0.5" />
                          <span className="text-charcoal/80">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {isCurrentPlan ? (
                      <button disabled className="w-full btn btn-secondary opacity-50 cursor-not-allowed">
                        Current Plan
                      </button>
                    ) : subscription ? (
                      <button
                        onClick={() => handleChangePlan(plan.tier)}
                        disabled={isProcessing}
                        className={`w-full btn ${isUpgrade ? 'btn-primary' : 'btn-secondary'}`}
                      >
                        {isProcessing ? 'Processing...' : (
                          <>
                            {isUpgrade ? (
                              <><ArrowUpIcon className="w-4 h-4 mr-1" /> Upgrade</>
                            ) : (
                              <><ArrowDownIcon className="w-4 h-4 mr-1" /> Downgrade</>
                            )}
                          </>
                        )}
                      </button>
                    ) : plan.tier !== 'starter' ? (
                      <button
                        onClick={() => handleSubscribe(plan.tier)}
                        disabled={isProcessing}
                        className="w-full btn btn-primary"
                      >
                        {isProcessing ? 'Processing...' : 'Subscribe'}
                      </button>
                    ) : (
                      <button disabled className="w-full btn btn-secondary opacity-50 cursor-not-allowed">
                        Free Plan
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Enterprise CTA */}
            <div className="p-6 border-t border-tan/30">
              <div className="flex items-center justify-between p-4 bg-cream/50 rounded-lg">
                <div>
                  <h4 className="font-semibold text-charcoal">Enterprise</h4>
                  <p className="text-sm text-charcoal/60">Custom solutions for large organizations</p>
                </div>
                <a
                  href="mailto:sales@venndela.com?subject=Enterprise%20Plan%20Inquiry"
                  className="btn btn-secondary"
                >
                  Contact Sales
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
